import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { BASIS_LABEL, ParcelHit, formatNumber, potentialOf } from './ParcelSearchAPI';

/**
 * Die Treffer der Grundstückssuche als Tabelle unter der Karte. Ein Klick auf eine Zeile rückt das Flurstück auf der
 * Karte ins Bild; ein Klick auf der Karte markiert die Zeile. Weitere Treffer seitenweise.
 */

interface Properties {
    rows: ParcelHit[];
    total: number;
    totalCapped: boolean;
    selectedId: string | null;
    loadingMore: boolean;
    onSelect: (hit: ParcelHit) => void;
    onMore: () => void;
}

const head = 'sticky top-0 z-10 bg-card px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground';
const cell = 'px-2 py-1.5 align-top';
const numeric = 'text-right tabular-nums';

export default function SearchResultsTable({ rows, total, totalCapped, selectedId, loadingMore, onSelect, onMore }: Properties) {
    const { t } = useTranslation();
    const selectedRef = useRef<HTMLTableRowElement | null>(null);
    useEffect(() => {
        selectedRef.current?.scrollIntoView({ block: 'nearest' });
    }, [selectedId]);

    return (
        <div className="flex min-h-0 flex-col overflow-hidden rounded-2xl border bg-card shadow-sm">
            <div className="flex items-center justify-between border-b px-3 py-2 text-sm">
                <span>
                    {t('heatmap.search.summary', {
                        total: totalCapped ? t('heatmap.search.overTenThousand') : formatNumber(total),
                        shown: rows.length,
                    })}
                </span>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
                <table className="w-full border-collapse text-sm">
                    <thead>
                        <tr>
                            <th className={head}>{t('heatmap.search.columns.location')}</th>
                            <th className={head}>{t('heatmap.search.columns.parcel')}</th>
                            <th className={cn(head, 'text-right')}>{t('heatmap.search.columns.area')}</th>
                            <th className={head}>{t('heatmap.search.columns.rights')}</th>
                            <th className={cn(head, 'text-right')}>{t('heatmap.search.columns.existing')}</th>
                            <th className={cn(head, 'text-right')}>{t('heatmap.search.columns.reserve')}</th>
                            <th className={head}>{t('heatmap.search.columns.potential')}</th>
                            <th className={cn(head, 'text-right')}>{t('heatmap.search.columns.pv')}</th>
                            <th className={cn(head, 'text-right')}>{t('heatmap.search.columns.brw')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((hit) => (
                            <tr
                                key={hit._id}
                                ref={hit._id === selectedId ? selectedRef : undefined}
                                className={cn('cursor-pointer border-b hover:bg-accent', hit._id === selectedId && 'bg-accent font-semibold')}
                                onClick={() => onSelect(hit)}
                            >
                                <td className={cell}>{hit.lagebez ?? '–'}</td>
                                <td className={cell}>
                                    {[hit.number, hit.gemarkung].filter(Boolean).join(', ') || hit._id}
                                </td>
                                <td className={cn(cell, numeric)}>{formatNumber(hit.areaM2)}</td>
                                <td className={cell}>
                                    {BASIS_LABEL[hit.rights?.basis] ?? '–'}
                                    {hit.rights?.uses?.length ? ` · ${hit.rights.uses.join('/')}` : ''}
                                </td>
                                <td className={cn(cell, numeric)}>{formatNumber(hit.existing?.floorAreaM2)}</td>
                                <td className={cn(cell, numeric, 'font-semibold')}>{formatNumber(hit.reserveFloorAreaM2)}</td>
                                <td className={cell}>{potentialOf(hit)}</td>
                                <td className={cn(cell, numeric)}>{hit.pv?.yieldKWh ? formatNumber(hit.pv.yieldKWh) : '–'}</td>
                                <td className={cn(cell, numeric)}>{hit.brw?.value ? formatNumber(hit.brw.value) : '–'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {rows.length < total && (
                    <div className="flex justify-center p-2">
                        <Button type="button" variant="outline" size="sm" disabled={loadingMore} onClick={onMore}>
                            {loadingMore && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
                            {t('heatmap.search.more')}
                        </Button>
                    </div>
                )}
            </div>
            <p className="border-t px-3 py-1.5 text-xs text-muted-foreground">{t('heatmap.search.footnote')}</p>
        </div>
    );
}
